import * as React from "react";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import { Dialog, DialogContent, Switch } from "@mui/material";
import { isString } from "lodash";
import { useEffect, useState } from "react";
import { ArrowDownward, ArrowUpward } from "@mui/icons-material";
import parse from "html-react-parser"; // To show sorting icons
import { Link } from "react-router-dom";

interface SearchValues {
  [key: string]: string;
}

// @ts-ignore
const CustomTable = ({ rows, columns, numberOfRows }) => {
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(numberOfRows);
  const [isPopupOpen, setPopupOpen] = useState(false);
  const [popupContent, setPopupContent] = useState<any>("");
  const [searchValues, setSearchValues] = useState<SearchValues>({});

  const [hoveredColumn, setHoveredColumn] = useState(null);

  // State for sorting
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: "asc" | "desc";
  }>({
    key: "",
    direction: "asc",
  });

  // Handle page change
  const handleChangePage = (
    _: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number,
  ) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  // Handle search input change
  const handleSearchChange = (event: any, columnId: any) => {
    const { value } = event.target;
    setSearchValues((prevSearchValues: any) => ({
      ...prevSearchValues,
      [columnId]: value,
    }));
  };

  // Handle sorting logic
  const handleSort = (columnId: string) => {
    let direction: "asc" | "desc" = "asc";
    if (sortConfig.key === columnId && sortConfig.direction === "asc") {
      direction = "desc";
    }

    setSortConfig({ key: columnId, direction });
  };

  // Sort rows based on the current sortConfig
  const sortedRows = React.useMemo(() => {
    const sortedData = [...rows];
    const { key, direction } = sortConfig;
    if (key) {
      sortedData.sort((a, b) => {
        if (a[key] < b[key]) {
          return direction === "asc" ? -1 : 1;
        }
        if (a[key] > b[key]) {
          return direction === "asc" ? 1 : -1;
        }
        return 0;
      });
    }
    return sortedData;
  }, [rows, sortConfig]);

  useEffect(() => {
    setPage(0);
    setRowsPerPage(numberOfRows);
    setPopupOpen(false);
    setPopupContent("");
  }, [rows, columns, numberOfRows]);

  const handleCellClick = (content: any, row: any) => {
    if (row["doi"]) {
      setPopupContent(
        <p>
          {"unique_id: " + content} <br />
          <a
            href={`https://doi.org/${row["doi"]}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {`source: ${row["doi"]}`}
          </a>
        </p>,
      );
    } else {
      setPopupContent(content);
    }
    setPopupOpen(true);
  };

  return (
    <div>
      {rows.length > 0 && columns.length > 0 ? (
        <Paper sx={{ width: "100%", padding: "1rem 1rem 0 1rem" }}>
          <TableContainer sx={{ maxHeight: 440, margin: 0 }}>
            <Table stickyHeader size="small" aria-label="sticky table">
              <TableHead>
                <TableRow>
                  {columns.map((column: any) => {
                    const isSorted = column.id === sortConfig.key;
                    const sortDirection = isSorted
                      ? sortConfig.direction
                      : null;
                    return (
                      <TableCell
                        key={column.id}
                        align={column.align}
                        className="bg-orange-500 text-white cursor-pointer"
                        onClick={() => handleSort(column.id)}
                        onMouseEnter={() => setHoveredColumn(column.id)}
                        onMouseLeave={() => setHoveredColumn(null)}
                        style={{
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          fontWeight: "bold",
                        }}
                      >
                        {parse(column.label)}
                        {(isSorted || hoveredColumn === column.id) && (
                          <span>
                            {sortDirection === "asc" ? (
                              <ArrowUpward fontSize="small" />
                            ) : (
                              <ArrowDownward fontSize="small" />
                            )}
                          </span>
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedRows
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row) => {
                    return (
                      <TableRow
                        hover
                        role="checkbox"
                        tabIndex={-1}
                        key={row.id}
                      >
                        {columns.map((column: any) => {
                          if (column.label === "active") {
                            const isStatusActive = row[column.id];
                            return (
                              <TableCell
                                key={column.id}
                                align={column.align}
                                className="p-0 m-0"
                              >
                                <Switch checked={isStatusActive} />
                              </TableCell>
                            );
                          } else if (column.label === "Unique ID") {
                            const value = row[column.id],
                              href = `/compositions/${value}`;
                            return (
                              <TableCell
                                key={column.id}
                                align={column.align}
                                className="p-0 m-0"
                                style={{ width: "15%" }}
                              >
                                {/* <span
                                  onClick={() =>
                                    handleCellClick(value, row)
                                  }
                                  className="cursor-pointer hover:text-blue-500"
                                >
                                  {isString(value)
                                    ? parse(value)
                                    : value}
                                </span> */}
                                <Link to={href}>{value}</Link>
                              </TableCell>
                            );
                          } else {
                            let value = row[column.id];
                            if (column.id === "Ductility [%]") {
                              value =
                                row[column.id] && row["Test Type"]
                                  ? `${row[column.id]} | ${row["Test Type"]}`
                                  : "";
                            }
                            let renderedValue =
                              typeof value === "object"
                                ? JSON.stringify(value)
                                : value;
                            return (
                              <TableCell
                                key={column.id}
                                align={column.align}
                                className="p-0 m-0"
                              >
                                {isString(renderedValue)
                                  ? parse(renderedValue)
                                  : renderedValue}
                              </TableCell>
                            );
                          }
                        })}
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>

            <Dialog
              open={isPopupOpen}
              onClose={() => setPopupOpen(false)}
              maxWidth="md"
              fullWidth
            >
              <DialogContent>{popupContent}</DialogContent>
            </Dialog>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[50, 100]}
            component="div"
            count={rows.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      ) : (
        <></>
      )}
    </div>
  );
};

export default CustomTable;
